import { useState } from 'react';
import { Mail, Eye, X } from 'lucide-react';
import type { EmailLog } from '@peles/shared';
import { useEmailLogs } from '../hooks/useEmails';

export function EmailLogsPage() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [templateFilter, setTemplateFilter] = useState<string>('');
  const [searchTo, setSearchTo] = useState<string>('');
  const [previewEmail, setPreviewEmail] = useState<EmailLog | null>(null);

  const { data, isLoading } = useEmailLogs({
    status: statusFilter || undefined,
    template: templateFilter || undefined,
    to: searchTo || undefined,
    limit: 50,
  });

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      SIMULATED: 'bg-yellow-100 text-yellow-800',
      SENT: 'bg-green-100 text-green-800',
      FAILED: 'bg-red-100 text-red-800',
    };
    return (
      <span
        className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Mail size={24} />
        <h1 className="text-2xl font-bold">Email Logs</h1>
      </div>

      {/* Filters */}
      <div
        className="flex flex-wrap gap-3 bg-white rounded-lg border p-4"
        data-testid="email-filters"
      >
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm"
          data-testid="status-filter"
        >
          <option value="">All Statuses</option>
          <option value="SIMULATED">Simulated</option>
          <option value="SENT">Sent</option>
          <option value="FAILED">Failed</option>
        </select>
        <select
          value={templateFilter}
          onChange={(e) => setTemplateFilter(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm"
          data-testid="template-filter"
        >
          <option value="">All Templates</option>
          <option value="event-update">Event Update</option>
          <option value="quote-received">Quote Received</option>
          <option value="payment-reminder">Payment Reminder</option>
          <option value="invitation">Invitation</option>
          <option value="welcome">Welcome</option>
        </select>
        <input
          type="text"
          placeholder="Search by recipient..."
          value={searchTo}
          onChange={(e) => setSearchTo(e.target.value)}
          className="px-3 py-2 border rounded-md text-sm flex-1 min-w-[200px]"
          data-testid="to-search"
        />
      </div>

      {/* Table */}
      {isLoading ? (
        <p data-testid="loading">Loading email logs...</p>
      ) : (
        <div className="bg-white rounded-lg border overflow-hidden">
          <table className="w-full" data-testid="email-logs-table">
            <thead>
              <tr className="bg-gray-50 text-left text-sm text-gray-600">
                <th className="px-4 py-2">Date</th>
                <th className="px-4 py-2">To</th>
                <th className="px-4 py-2">Subject</th>
                <th className="px-4 py-2">Template</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data?.logs.map((log) => (
                <tr key={log.id} className="border-t text-sm" data-testid={`email-row-${log.id}`}>
                  <td className="px-4 py-2">{new Date(log.sentAt).toLocaleString()}</td>
                  <td className="px-4 py-2">{log.to}</td>
                  <td className="px-4 py-2 max-w-[200px] truncate">{log.subject}</td>
                  <td className="px-4 py-2">{log.template}</td>
                  <td className="px-4 py-2">{statusBadge(log.status)}</td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => setPreviewEmail(log)}
                      className="text-primary-600 hover:text-primary-800"
                      data-testid={`preview-${log.id}`}
                      title="Preview email"
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))}
              {(!data?.logs || data.logs.length === 0) && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                    No email logs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {data && data.total > 0 && (
            <div className="px-4 py-2 border-t text-sm text-gray-500">
              Showing {data.logs.length} of {data.total} emails
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewEmail && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setPreviewEmail(null)}
          data-testid="preview-modal"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div>
                <h3 className="font-medium">{previewEmail.subject}</h3>
                <p className="text-sm text-gray-500">To: {previewEmail.to}</p>
              </div>
              <button
                onClick={() => setPreviewEmail(null)}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-preview"
              >
                <X size={20} />
              </button>
            </div>
            <div
              className="p-4 overflow-auto flex-1"
              dangerouslySetInnerHTML={{ __html: previewEmail.htmlBody }}
              data-testid="email-preview-body"
            />
          </div>
        </div>
      )}
    </div>
  );
}
